import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { FieldTypes } from "@/api/AppApi/services/builder/interfaces/interfaces";
import { useContactUsPost } from "@/api/AppApi/services/contact-us";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useForm } from "react-hook-form";
import ContactUsSubtitle from "./ContactUsSubtitle";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "../../lib/CustomButton";
import { IDialCodeCountry } from "@/api/AppApi/services/company/interfaces/api-company-interfaces";
import {
  createCustomFields,
  formErrors,
} from "../../common-form-builder/form-builder-helpers/form-functions";
import HookInput from "../../common-form-builder/common-fields/hook-input/HookInput";
import HookPhoneNumber from "../../common-form-builder/common-fields/hook-phone-number/HookPhoneNumber";
import InputErrorMsg from "../../common-form-builder/form-builder-helpers/InputErrorMsg";
import AppToast from "../../lib/toast/AppToast";
import { useState, useRef } from "react";
import RequiredStar from "../../lib/RequiredStar";
import NotFieldErrModal from "../../common-form-builder/form-builder-helpers/NotFieldErrModal";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import {
  EventType,
  IGTagUserData,
} from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import { getFbcCookie, getFbpCookie } from "@/utils/functions/cookieOperations";
import { v4 as uuidv4 } from "uuid";
interface Fields {
  name: string;
  email: string;
  mobile: { number: string; dial: IDialCodeCountry };
  subject: string;
  message: string;
}
const ContactUsForm = () => {
  const t = useTranslation();
  const mobile = useIsMobile();
  const [showToast, setShowToast] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const hookForm = useForm<any>();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = hookForm;
  const FBEventIDRef = useRef<string>("");
  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);
  const { eventTrackers, GTagContactEventTracher } = useEventTrackers();
  const contactUsPost = useContactUsPost({
    onSuccess: (res) => {
      setResultMsg(t.your_message_was_sent_successfully);
      setShowToast(true);
      if (res?.message?.type === ApiMsgTypes.success) {
        hookForm.reset();
        const formUserData: IGTagUserData = {
          name: (hookForm.getValues() as any)?.name,
          email: (hookForm.getValues() as any)?.email,
          phone: (hookForm.getValues() as any)?.mobile?.number,
        };
        const event_id = FBEventIDRef.current;
        GTagContactEventTracher(formUserData);
        eventTrackers(EventType.contact, [], {}, formUserData, event_id);
      }
    },
    onError: (error) => {
      setShowToast(true);
      setResultMsg(t.failed_to_send_the_message);

      const extraErr = formErrors(error, hookForm);
      setNotFieldErr(extraErr);
    },
  });
  const onSubmit = (data: Fields) => {
    const fbc = (getFbcCookie() as string) ?? null;
    const fbp = (getFbpCookie() as string) ?? null;
    const event_id = uuidv4();
    FBEventIDRef.current = event_id;
    contactUsPost.mutate({
      body: {
        ...data,
        mobile: {
          number: data.mobile.number,
          country: data.mobile?.dial?.iso2.toUpperCase(),
        },
      },
      params: { fbc, fbp, event_id },
    });
  };
  const fields = createCustomFields([
    {
      key: "mobile",
      type: FieldTypes.phone,
      title: t.contact_number,
      is_required: true,
      value: null,
    },
  ]);
  const requiredMsg = t.this_field_is_required;
  return (
    <>
      {showToast && (
        <AppToast
          top
          show={showToast}
          setShow={setShowToast}
          message={<div>{resultMsg}</div>}
        />
      )}
      {notFieldErr.length > 0 && (
        <NotFieldErrModal errors={notFieldErr} setErrors={setNotFieldErr} />
      )}
      <div>
        <ContactUsSubtitle text={t.email_technical_support} />
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={mobile ? { gap: 16 } : { gap: 24 }}
        >
          <div className="contact-us-form">
            <HookInput
              required
              label={t.name}
              inputRegister={{
                ...register("name", {
                  required: requiredMsg,
                }),
              }}
              errorMsg={errors?.name?.message as string}
            />
            <HookInput
              required
              label={t.e_mail}
              inputRegister={{
                ...register("email", {
                  required: requiredMsg,
                }),
              }}
              errorMsg={errors?.email?.message as string}
            />
            <HookPhoneNumber form={hookForm} field={fields[0]} />
            <HookInput
              label={t.subject}
              required
              inputRegister={{
                ...register("subject", {
                  required: requiredMsg,
                }),
              }}
              errorMsg={errors?.subject?.message as string}
            />
            <div>
              <div className="builder-item-label" style={{ marginBottom: 0 }}>
                <RequiredStar title={t.message} isRequired={true} />
              </div>
              <textarea
                {...register("message", {
                  required: requiredMsg,
                })}
                placeholder={t.write_your_message_here}
                className="contact-textarea"
              />
              {errors.message && (
                <InputErrorMsg
                  errorMsg={(errors.message?.message as string) ?? ""}
                />
              )}
            </div>
          </div>
          <div className={`mt-4 ${mobile ? "w-100" : "w-50"}`}>
            <CustomButton
              type={BtnTypes.primary}
              size={BtnSizes.large}
              radius={BtnRadius.small}
              onClick={() => {}}
              fullWidth
              isSubmit
            >
              {t.send}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default ContactUsForm;
